import { Component, OnInit, ViewChild, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CartService, CartItemCustomization } from '../../services/cart.service';
import { CartDisplayComponent } from '../cart-display/cart-display.component';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import { SnakeGameComponent } from '../snake-game/snake-game.component';
import { PwaInstallService } from '../../services/pwa-install.service';
import { EntertainmentService } from '../../services/entertainment.service';
import { ConfigService } from '../../services/config.service';

interface MenuCategory {
  id: number;
  name: string;
  parent_id?: number;
  color?: string;
  icon?: string;
  product_count?: number;
  description?: string;
}

interface ProductVariant {
  id: number;
  name: string;
  price: number;
  sort_order: number;
}

interface IngredientOption {
  id: number;
  name: string;
  allergens?: string[];
}

interface IngredientExtra {
  id: number;
  name: string;
  extra_price?: string;
  allergens?: string[];
}

interface MenuProduct {
  id: number;
  name: string;
  category_id: number;
  category: string;
  price?: number;
  description?: string;
  image_url?: string;
  available: boolean;
  allergens?: string[];
  ingredients_optional?: IngredientOption[];
  ingredients_extras?: IngredientExtra[];
  stock?: number | null;
  variants?: ProductVariant[];
  has_variants?: boolean;
}

interface PackSectionItem {
  product_id: number;
  product_name: string;
  product_price?: string;
  category_name?: string;
  supplement_price: number | string | null;
  sort_order?: number;
}

interface PackSection {
  name: string;
  max_choices: number;
  min_choices?: number; // 0 = optional step, 1+ = required
  items: PackSectionItem[];
}

interface MenuPack {
  id: number;
  name: string;
  description?: string;
  pack_type: string;
  pack_price: string;
  discount_value?: string | null;
  available_from?: string | null;
  available_until?: string | null;
  max_uses_per_day?: number | null;
  sections: PackSection[];
}

interface PackSelection {
  item: PackSectionItem;
  removedIngredients: Set<number>;
  selectedExtras: Set<number>;
}

interface RestaurantInfo {
  id: string;
  name: string;
  description?: string;
  location?: any;
  selectedTerminal?: {
    name: string;
    suffix: string;
  };
}

interface MenuData {
  active: boolean;
  categories: MenuCategory[];
  products: MenuProduct[];
  company_hash: string;
  restaurant_name: string;
  terminal_name: string;
  terminal_suffix: string;
  last_updated: string;
}

type ViewMode = 'categories' | 'subcategories' | 'products';

@Component({
  selector: 'app-restaurant-menu',
  standalone: true,
  imports: [CommonModule, FormsModule, CartDisplayComponent, ThemeToggleComponent, SnakeGameComponent],
  templateUrl: './restaurant-menu.component.html',
  styleUrl: './restaurant-menu.component.scss'
})
export class RestaurantMenuComponent implements OnInit {
  private router = inject(Router);
  private cartService = inject(CartService);
  private pwaService = inject(PwaInstallService);
  private configService = inject(ConfigService);
  public entertainmentService = inject(EntertainmentService);

  // ViewChild references
  @ViewChild('snakeGame') snakeGame!: SnakeGameComponent;

  // Signals de Estado
  viewMode = signal<ViewMode>('categories');
  selectedMainCategory = signal<MenuCategory | null>(null);
  selectedSubCategory = signal<MenuCategory | null>(null);
  categories = signal<MenuCategory[]>([]);
  products = signal<MenuProduct[]>([]);
  categoryPath = signal<MenuCategory[]>([]);
  restaurantInfo = signal<RestaurantInfo | null>(null);
  menuData = signal<MenuData | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);
  private menuSource: string | null = null; // 'business-detail' | 'qr-scan' | 'public-business'

  // View mode for products — default list, grid for when there are images
  productViewMode = signal<'grid' | 'list'>('list');

  // Cart related signals
  selectedProduct = signal<MenuProduct | null>(null);
  showCustomizationModal = signal(false);
  showNotesModal = signal(false);
  tempCustomizations = signal<CartItemCustomization[]>([]);
  tempNotes = signal('');
  tempNotesText = '';
  
  // Entertainment menu signals
  showEntertainmentMenu = computed(() => this.entertainmentService.isDropdownOpen());

  // Computed Values
  mainCategories = computed(() => 
    this.categories().filter(cat => !cat.parent_id || cat.parent_id === 0)
  );

  subCategories = computed(() => {
    const mainCat = this.selectedMainCategory();
    return mainCat ? this.categories().filter(cat => cat.parent_id === parseInt(mainCat.id.toString())) : [];
  });

  filteredProducts = computed(() => {
    const subCat = this.selectedSubCategory();
    const mainCat = this.selectedMainCategory();

    if (subCat) {
      return this.products().filter(p => p.category === subCat.name);
    }
    if (mainCat) {
      // Si no hay subcategorías, mostrar productos de la categoría principal
      const hasSubcategories = this.subCategories().length > 0;
      if (!hasSubcategories) {
        return this.products().filter(p => p.category === mainCat.name);
      }
      // Si hay subcategorías, mostrar productos de todas las subcategorías
      const subCatNames = this.subCategories().map(sub => sub.name);
      return this.products().filter(p => subCatNames.includes(p.category));
    }
    return this.products();
  });

  availableProducts = computed(() => 
    this.filteredProducts().filter(p => p.available !== false)
  );

  breadcrumbs = computed(() => {
    const crumbs: { name: string, action: () => void }[] = [];
    const info = this.restaurantInfo();

    // First crumb: business name → back to business detail
    if (info?.name) {
      crumbs.push({
        name: info.name,
        action: () => this.goBack()
      });
    }

    crumbs.push({
      name: 'Carta',
      action: () => this.goBackToCategories()
    });

    const mainCat = this.selectedMainCategory();
    if (mainCat) {
      crumbs.push({
        name: mainCat.name,
        action: () => this.goBackToMainCategory()
      });
    }

    const subCat = this.selectedSubCategory();
    if (subCat) {
      crumbs.push({
        name: subCat.name,
        action: () => {}
      });
    }

    return crumbs;
  });

  // Cart computed values
  cart = computed(() => this.cartService.cart());
  cartTotalItems = computed(() => this.cartService.totalItems());
  cartTotalPrice = computed(() => this.cartService.totalPrice());

  ngOnInit(): void {
    this.loadMenuFromNavigation();
    this.initializeCartForRestaurant();
  }

  private loadMenuFromNavigation(): void {
    const navigation = this.router.getCurrentNavigation();
    const state = history.state;

    if (state && state.restaurant && state.menu) {
      console.log('📋 Loading menu from navigation state:', state);

      this.menuSource = state.source || null;
      this.restaurantInfo.set(state.restaurant);
      this.menuData.set(state.menu);
      this.processMenuData(state.menu);
    } else {
      // Try to recover from sessionStorage (after login redirect)
      const saved = sessionStorage.getItem('barclik_public_menu');
      if (saved) {
        try {
          const data = JSON.parse(saved);
          console.log('📋 Loading menu from sessionStorage (post-login):', data);
          this.restaurantInfo.set(data.restaurant);
          this.menuData.set(data.menu);
          this.processMenuData(data.menu);
          return;
        } catch { /* ignore */ }
      }
      console.error('❌ No menu data found');
      this.error.set('No se encontraron datos del menú');
    }
  }

  private processMenuData(menu: MenuData): void {
    console.log('🔄 Processing menu data:', menu);

    // Manejar tanto arrays como objetos para compatibilidad
    const categories = Array.isArray(menu.categories) ? menu.categories : [];
    const rawProducts = Array.isArray(menu.products) ? menu.products : [];

    // Ensure has_variants is set based on variants array
    const products: MenuProduct[] = rawProducts.map((p: any) => {
      const rawVariants = Array.isArray(p.variants) && p.variants.length > 0 ? p.variants : undefined;
      const variants = rawVariants?.sort((a: any, b: any) => (a.price || 0) - (b.price || 0));
      return {
        ...p,
        variants,
        has_variants: !!variants,
      };
    });

    const variantProducts = products.filter(p => p.has_variants);
    const withIngredients = products.filter(p => (p.ingredients_optional?.length || 0) > 0 || (p.ingredients_extras?.length || 0) > 0);
    const packsArr = Array.isArray((menu as any).packs) ? (menu as any).packs : [];
    console.log('📊 Menu data:', {
      categories: categories.length,
      products: products.length,
      withVariants: variantProducts.length,
      withIngredients: withIngredients.length,
      packs: packsArr.length,
      sampleProduct: products[0] ? { name: products[0].name, ingredients_optional: products[0].ingredients_optional, ingredients_extras: products[0].ingredients_extras, allergens: products[0].allergens } : null
    });
    
    // Count products per category (including subcategory products for parent categories)
    const categoriesWithCounts = categories.map((category: any) => {
      // Direct products in this category
      let count = products.filter((p: any) => p.category === category.name && p.available !== false).length;

      // If parent category (no parent_id), also count products in subcategories
      if (!category.parent_id) {
        const subCatNames = categories
          .filter((c: any) => c.parent_id === category.id || c.parent_id === String(category.id))
          .map((c: any) => c.name);
        for (const subName of subCatNames) {
          count += products.filter((p: any) => p.category === subName && p.available !== false).length;
        }
      }

      // Fix icon — use emoji if it's a CSS class
      const icon = (category.icon && category.icon.startsWith('fas ')) ? '' : (category.icon || '');

      return { ...category, product_count: count, icon };
    });

    this.categories.set(categoriesWithCounts);
    this.products.set(products);

    // Load packs
    const menuPacks = Array.isArray((menu as any).packs) ? (menu as any).packs : [];
    this.packs.set(menuPacks);
    if (menuPacks.length > 0) {
      console.log('🎁 Packs loaded:', menuPacks.length);
    }
    
    console.log('✅ Menu processed:', {
      categories: categoriesWithCounts.length,
      products: products.length,
      mainCategories: this.mainCategories().length
    });
  }

  // Navegación Methods
  selectMainCategory(category: MenuCategory): void {
    // Toggle: if already selected, deselect
    if (this.selectedMainCategory()?.id === category.id) {
      this.selectedMainCategory.set(null);
      this.selectedSubCategory.set(null);
      this.viewMode.set('categories');
    } else {
      this.selectedMainCategory.set(category);
      this.selectedSubCategory.set(null);
      this.viewMode.set('products');
    }
    this.updateCategoryPath();
  }

  selectSubCategory(subCategory: MenuCategory): void {
    // Toggle: if already selected, deselect
    if (this.selectedSubCategory()?.id === subCategory.id) {
      this.selectedSubCategory.set(null);
    } else {
      this.selectedSubCategory.set(subCategory);
    }
    this.viewMode.set('products');
    this.updateCategoryPath();
  }

  goBackToCategories(): void {
    this.selectedMainCategory.set(null);
    this.selectedSubCategory.set(null);
    this.viewMode.set('categories');
    this.categoryPath.set([]);
  }

  goBackToMainCategory(): void {
    this.selectedSubCategory.set(null);
    this.viewMode.set('products');
    this.updateCategoryPath();
  }

  private updateCategoryPath(): void {
    const path: MenuCategory[] = [];
    
    const mainCat = this.selectedMainCategory();
    if (mainCat) path.push(mainCat);
    
    const subCat = this.selectedSubCategory();
    if (subCat) path.push(subCat);
    
    this.categoryPath.set(path);
  }

  // Utility Methods
  getCategoryColor(category: MenuCategory): string {
    return category.color || '#6B7280';
  }

  getCategoryIcon(category: MenuCategory): string {
    return category.icon || 'fas fa-folder';
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  }

  // Variant expansion
  expandedVariantProductId: number | null = null;

  // Quick add modal
  addModalProduct: MenuProduct | null = null;
  addModalVariant: ProductVariant | null = null;
  addModalNote = '';
  addModalShowNote = false;
  addModalRemovedIngredients = new Set<number>();
  addModalSelectedExtras = new Set<number>();

  onProductTap(product: MenuProduct): void {
    console.log('TAP:', product.name, 'has_variants:', product.has_variants);
    if (!product.available) return;
    this.openAddModal(product);
  }

  openAddModal(product: MenuProduct): void {
    this.addModalProduct = product;
    this.addModalVariant = null;
    this.addModalNote = '';
    this.addModalShowNote = false;
    this.addModalRemovedIngredients = new Set<number>();
    this.addModalSelectedExtras = new Set<number>();
  }

  openAddModalVariant(product: MenuProduct, variant: ProductVariant): void {
    this.addModalProduct = product;
    this.addModalVariant = variant;
    this.addModalNote = '';
  }

  selectModalVariant(variant: ProductVariant): void {
    this.addModalVariant = variant;
  }

  closeAddModal(): void {
    this.addModalProduct = null;
    this.addModalVariant = null;
    this.addModalNote = '';
    this.addModalShowNote = false;
  }

  toggleRemoveIngredient(id: number): void {
    if (this.addModalRemovedIngredients.has(id)) {
      this.addModalRemovedIngredients.delete(id);
    } else {
      this.addModalRemovedIngredients.add(id);
    }
  }

  toggleExtra(id: number): void {
    if (this.addModalSelectedExtras.has(id)) {
      this.addModalSelectedExtras.delete(id);
    } else {
      this.addModalSelectedExtras.add(id);
    }
  }

  get hasCustomizations(): boolean {
    if (!this.addModalProduct) return false;
    return (this.addModalProduct.ingredients_optional?.length || 0) > 0
      || (this.addModalProduct.ingredients_extras?.length || 0) > 0;
  }

  get modalTotalPrice(): number {
    if (!this.addModalProduct) return 0;
    let base = this.addModalVariant ? this.addModalVariant.price : (this.addModalProduct.price || 0);
    // Add selected extras prices
    if (this.addModalProduct.ingredients_extras) {
      for (const extra of this.addModalProduct.ingredients_extras) {
        if (this.addModalSelectedExtras.has(extra.id)) {
          base += parseFloat(extra.extra_price || '0');
        }
      }
    }
    return base;
  }

  confirmAddModal(): void {
    if (!this.addModalProduct) return;
    const product = this.addModalProduct;
    const variant = this.addModalVariant;
    const note = this.addModalNote.trim();

    // Build customizations array
    const customizations: any[] = [];

    // Removed ingredients
    if (product.ingredients_optional) {
      for (const ing of product.ingredients_optional) {
        if (this.addModalRemovedIngredients.has(ing.id)) {
          customizations.push({ type: 'remove', ingredient: ing.name, description: `Sin ${ing.name}` });
        }
      }
    }

    // Added extras
    if (product.ingredients_extras) {
      for (const extra of product.ingredients_extras) {
        if (this.addModalSelectedExtras.has(extra.id)) {
          customizations.push({ type: 'add', ingredient: extra.name, description: `Extra ${extra.name}` });
        }
      }
    }

    const productId = variant ? `${product.id}-v${variant.id}` : product.id.toString();
    const productName = variant ? `${product.name} (${variant.name})` : product.name;
    const price = this.modalTotalPrice;

    // Collect extra prices for order submission
    const extraPrices: { name: string; price: number }[] = [];
    if (product.ingredients_extras) {
      for (const extra of product.ingredients_extras) {
        if (this.addModalSelectedExtras.has(extra.id) && extra.extra_price) {
          extraPrices.push({ name: extra.name, price: parseFloat(extra.extra_price) || 0 });
        }
      }
    }

    this.cartService.addItem(
      productId,
      productName,
      price,
      product.category,
      customizations,
      note,
      product.image_url,
      {
        real_product_id: typeof product.id === 'string' ? parseInt(product.id, 10) : product.id,
        variant_id: variant?.id,
        variant_name: variant?.name,
        extra_prices: extraPrices.length > 0 ? extraPrices : undefined,
      }
    );
    this.closeAddModal();
  }

  // Actions
  addToCart(product: MenuProduct): void {
    if (product.has_variants && product.variants && product.variants.length > 0) {
      this.toggleVariantExpand(product.id);
      return;
    }

    if (product.description && product.description.includes('personalizable')) {
      this.showCustomizationModalForProduct(product);
    } else {
      this.cartService.addItem(
        product.id.toString(),
        product.name,
        product.price || 0,
        product.category,
        [],
        '',
        product.image_url
      );
    }
  }

  toggleVariantExpand(productId: number): void {
    this.expandedVariantProductId = this.expandedVariantProductId === productId ? null : productId;
  }

  isVariantExpanded(productId: number): boolean {
    return this.expandedVariantProductId === productId;
  }

  addVariantToCart(product: MenuProduct, variant: ProductVariant): void {
    this.cartService.addItem(
      `${product.id}-v${variant.id}`,
      `${product.name} (${variant.name})`,
      variant.price,
      product.category,
      [],
      '',
      product.image_url
    );
  }

  getVariantQuantityInCart(productId: number, variantId: number): number {
    const cart = this.cartService.cart();
    if (!cart) return 0;
    const itemId = `${productId}-v${variantId}`;
    return cart.items.filter(i => i.product_id === itemId).reduce((sum, i) => sum + i.quantity, 0);
  }

  decreaseVariantQuantity(productId: number, variantId: number): void {
    const cart = this.cartService.cart();
    if (!cart) return;
    const itemId = `${productId}-v${variantId}`;
    const item = cart.items.find(i => i.product_id === itemId);
    if (item) {
      this.cartService.removeItem(item.id);
    }
  }

  viewProductDetails(product: MenuProduct): void {
    console.log('👁️ Viewing product details:', product.name);
    this.showCustomizationModalForProduct(product);
  }

  // Cart Management Methods
  private initializeCartForRestaurant(): void {
    const restaurantInfo = this.restaurantInfo();
    const menu = this.menuData();
    if (!restaurantInfo) return;

    // Usar company_hash del menú como ID del restaurante (requerido por el TPV)
    const restaurantId = menu?.company_hash || restaurantInfo.id;
    const terminalSuffix = menu?.terminal_suffix || restaurantInfo.selectedTerminal?.suffix || 'default';

    // Verificar si ya hay un carrito para este restaurante
    const isCurrentRestaurant = this.cartService.isCartForRestaurant(
      restaurantId,
      terminalSuffix
    );

    if (!isCurrentRestaurant) {
      // Inicializar nuevo carrito para este restaurante
      this.cartService.initializeCart(
        restaurantId,
        menu?.restaurant_name || restaurantInfo.name,
        terminalSuffix
      );
    }
  }

  showCustomizationModalForProduct(product: MenuProduct): void {
    this.selectedProduct.set(product);
    this.tempCustomizations.set([]);
    this.tempNotes.set('');
    this.showCustomizationModal.set(true);
  }

  closeCustomizationModal(): void {
    this.showCustomizationModal.set(false);
    this.selectedProduct.set(null);
    this.tempCustomizations.set([]);
    this.tempNotes.set('');
  }

  addCustomization(type: 'remove' | 'add' | 'change', ingredient: string, description: string): void {
    const currentCustomizations = this.tempCustomizations();
    const newCustomization: CartItemCustomization = { type, ingredient, description };
    
    // Verificar si ya existe una personalización para este ingrediente
    const existingIndex = currentCustomizations.findIndex(c => c.ingredient === ingredient);
    
    if (existingIndex >= 0) {
      // Reemplazar personalización existente
      currentCustomizations[existingIndex] = newCustomization;
    } else {
      // Añadir nueva personalización
      currentCustomizations.push(newCustomization);
    }
    
    this.tempCustomizations.set([...currentCustomizations]);
  }

  removeCustomization(index: number): void {
    const currentCustomizations = this.tempCustomizations();
    currentCustomizations.splice(index, 1);
    this.tempCustomizations.set([...currentCustomizations]);
  }

  addCustomizedProductToCart(): void {
    const product = this.selectedProduct();
    if (!product) return;

    this.cartService.addItem(
      product.id.toString(),
      product.name,
      product.price || 0,
      product.category,
      this.tempCustomizations(),
      this.tempNotes(),
      product.image_url
    );

    this.closeCustomizationModal();
  }

  // Notes modal methods
  showNotesModalForProduct(product: MenuProduct): void {
    this.selectedProduct.set(product);
    this.tempNotesText = '';
    this.showNotesModal.set(true);
  }

  closeNotesModal(): void {
    this.showNotesModal.set(false);
    this.selectedProduct.set(null);
    this.tempNotesText = '';
  }

  addProductWithNotes(): void {
    const product = this.selectedProduct();
    if (!product) return;

    this.cartService.addItem(
      product.id.toString(),
      product.name,
      product.price || 0,
      product.category,
      [],
      this.tempNotesText,
      product.image_url
    );

    this.closeNotesModal();
  }

  // Quantity control methods
  getProductQuantityInCart(productId: number): number {
    return this.cartService.getItemQuantity(productId.toString());
  }

  increaseProductQuantity(product: MenuProduct): void {
    if (product.has_variants && product.variants && product.variants.length > 0) {
      this.toggleVariantExpand(product.id);
      return;
    }
    this.cartService.addItem(
      product.id.toString(),
      product.name,
      product.price || 0,
      product.category,
      [],
      '',
      product.image_url
    );
  }

  decreaseProductQuantity(product: MenuProduct): void {
    console.log('➖ Decreasing quantity for product:', product.name);
    const currentQuantity = this.getProductQuantityInCart(product.id);
    if (currentQuantity > 0) {
      // Buscar el primer item con este product_id para remover
      const cart = this.cartService.cart();
      if (cart) {
        const item = cart.items.find(item => item.product_id === product.id.toString());
        if (item) {
          this.cartService.removeItem(item.id);
        }
      }
    }
  }


  openChat(): void {
    console.log('💬 Opening chat - feature coming soon');
    // TODO: Implement chat functionality
  }

  getSnakeHighScore(): number {
    const saved = localStorage.getItem('snake-high-score');
    return saved ? parseInt(saved, 10) : 0;
  }

  // PWA Install methods
  shouldShowInstallButton(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const isiOS = /iphone|ipad|ipod/.test(userAgent);
    
    return (
      // Desktop/Android: Show if install available and not installed
      (!this.pwaService.isInstalled() && this.pwaService.isInstallAvailable()) ||
      // iOS: Always show if not installed (manual instructions)
      (!this.pwaService.isInstalled() && isiOS) ||
      // Already installed but opened in browser
      this.pwaService.isRunningInBrowser()
    );
  }

  getInstallButtonIcon(): string {
    if (this.pwaService.isRunningInBrowser()) {
      return 'fa-external-link-alt'; // Open in installed app
    }
    return 'fa-download'; // Install app
  }

  getInstallButtonTitle(): string {
    if (this.pwaService.isRunningInBrowser()) {
      return 'Abrir en la app instalada';
    }
    
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'Instalar BarClik (iOS)';
    } else if (/android/.test(userAgent)) {
      return 'Instalar BarClik (Android)';
    }
    return 'Instalar BarClik';
  }

  async handleInstallClick(): Promise<void> {
    if (this.pwaService.isRunningInBrowser()) {
      // Try to open in installed app
      this.pwaService.openInstalledApp();
      return;
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const isiOS = /iphone|ipad|ipod/.test(userAgent);

    if (isiOS) {
      // Show iOS installation instructions
      this.showIOSInstallInstructions();
    } else {
      // Try automatic installation for Android/Desktop
      const success = await this.pwaService.showInstallPrompt();
      if (!success) {
        // Fallback to manual instructions
        this.showManualInstallInstructions();
      }
    }
  }

  private showIOSInstallInstructions(): void {
    const instructions = this.pwaService.getPlatformInstructions();
    const message = `Para instalar BarClik en tu dispositivo:\n\n${instructions.instructions.join('\n')}`;
    alert(message);
  }

  private showManualInstallInstructions(): void {
    const instructions = this.pwaService.getPlatformInstructions();
    const message = `Para instalar BarClik:\n\n${instructions.instructions.join('\n')}`;
    alert(message);
  }


  // Snake Game methods
  openSnakeGame(): void {
    console.log('🎮 Opening Snake game');
    this.snakeGame.openGame();
  }

  toggleProductView(): void {
    this.productViewMode.set(this.productViewMode() === 'grid' ? 'list' : 'grid');
  }

  resolveImg(url: string | undefined): string {
    return this.configService.resolveImageUrl(url);
  }

  goBack(): void {
    // Navigate back to where the user came from
    const info = this.restaurantInfo();
    if (info?.id) {
      this.router.navigate(['/business', info.id]);
    } else {
      window.history.back();
    }
  }

  // ========== Pack Wizard ==========
  packs = signal<MenuPack[]>([]);
  packModalOpen = false;
  activePack: MenuPack | null = null;
  packSectionIndex = 0;
  packSelections: PackSelection[] = [];
  packNote = '';
  packShowNote = false;
  // Multi-selection state for current section
  packCurrentSelected = new Set<number>(); // product_ids toggled in current section
  packCustomizeQueue: PackSectionItem[] = [];
  packSelectionsPerSection: number[] = []; // count of PackSelection entries per completed section
  private packSelectionsSnapshot = 0; // packSelections.length before customize phase
  // Temp state for current section's item customization
  packTempItem: PackSectionItem | null = null;
  packTempRemoved = new Set<number>();
  packTempExtras = new Set<number>();

  get currentPackSection(): PackSection | null {
    if (!this.activePack) return null;
    return this.activePack.sections[this.packSectionIndex] || null;
  }

  get isPackSummaryStep(): boolean {
    if (!this.activePack) return false;
    return this.packSectionIndex >= this.activePack.sections.length;
  }

  get packTotalPrice(): number {
    if (!this.activePack) return 0;
    const pack = this.activePack;
    const type = pack.pack_type || 'fixed';

    if (type === 'fixed') {
      let total = parseFloat(pack.pack_price) || 0;
      for (const sel of this.packSelections) {
        if (sel.item.supplement_price) total += parseFloat(String(sel.item.supplement_price));
        const product = this.findProductById(sel.item.product_id);
        if (product?.ingredients_extras) {
          for (const extra of product.ingredients_extras) {
            if (sel.selectedExtras.has(extra.id)) total += parseFloat(extra.extra_price || '0');
          }
        }
      }
      return total;
    }

    // For non-fixed types, calculate from individual prices
    const prices = this.packSelections.map(s => parseFloat(String(s.item.product_price || 0)));
    const subtotal = prices.reduce((sum, p) => sum + p, 0);

    if (type === 'discount_percent') {
      return subtotal - subtotal * (parseFloat(String(pack.discount_value || 0)) / 100);
    }
    if (type === 'discount_amount') {
      return Math.max(0, subtotal - parseFloat(String(pack.discount_value || 0)));
    }
    if (type === 'cheapest_free' && prices.length > 0) {
      return subtotal - Math.min(...prices);
    }
    return subtotal;
  }

  isPackAvailable(pack: MenuPack): boolean {
    if (!pack.available_from && !pack.available_until) return true;
    const now = new Date();
    const current = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    if (pack.available_from && current < pack.available_from) return false;
    if (pack.available_until && current > pack.available_until) return false;
    return true;
  }

  getPackAvailabilityText(pack: MenuPack): string {
    if (pack.available_from && pack.available_until) return `Disponible de ${pack.available_from} a ${pack.available_until}`;
    if (pack.available_from) return `Disponible desde ${pack.available_from}`;
    if (pack.available_until) return `Disponible hasta ${pack.available_until}`;
    return '';
  }

  findProductById(productId: number): MenuProduct | null {
    return this.products().find(p => p.id === productId) || null;
  }

  openPackModal(pack: MenuPack): void {
    this.activePack = pack;
    this.packSectionIndex = 0;
    this.packSelections = [];
    this.packSelectionsPerSection = [];
    this.packCurrentSelected = new Set<number>();
    this.packCustomizeQueue = [];
    this.packSelectionsSnapshot = 0;
    this.packNote = '';
    this.packShowNote = false;
    this.packTempItem = null;
    this.packModalOpen = true;
  }

  closePackModal(): void {
    this.packModalOpen = false;
    this.activePack = null;
  }

  get isCurrentSectionOptional(): boolean {
    return (this.currentPackSection?.min_choices ?? 1) === 0;
  }

  get canAdvanceSection(): boolean {
    const section = this.currentPackSection;
    if (!section) return false;
    const min = section.min_choices ?? 1;
    return this.packCurrentSelected.size >= min;
  }

  get isMaxSelected(): boolean {
    const section = this.currentPackSection;
    if (!section) return false;
    return this.packCurrentSelected.size >= section.max_choices;
  }

  get sectionHint(): string {
    const section = this.currentPackSection;
    if (!section) return '';
    const min = section.min_choices ?? 1;
    const max = section.max_choices;
    if (min === max) return `Elige ${min}`;
    if (min === 0) return `Elige hasta ${max}`;
    return `Elige entre ${min} y ${max}`;
  }

  skipPackSection(): void {
    this.packSelectionsPerSection.push(0);
    this.packCurrentSelected = new Set<number>();
    this.packSectionIndex++;
  }

  togglePackSectionItem(item: PackSectionItem): void {
    const section = this.currentPackSection;
    if (!section) return;

    if (section.max_choices === 1) {
      // Radio behavior — clear and set
      this.packCurrentSelected.clear();
      this.packCurrentSelected.add(item.product_id);
    } else {
      // Checkbox behavior
      if (this.packCurrentSelected.has(item.product_id)) {
        this.packCurrentSelected.delete(item.product_id);
      } else if (this.packCurrentSelected.size < section.max_choices) {
        this.packCurrentSelected.add(item.product_id);
      }
    }
  }

  advancePackSection(): void {
    const section = this.currentPackSection;
    if (!section) return;

    const selectedItems = section.items.filter(i => this.packCurrentSelected.has(i.product_id));
    this.packSelectionsSnapshot = this.packSelections.length;

    // Separate items with/without customizations
    const needsCustom: PackSectionItem[] = [];
    for (const item of selectedItems) {
      const product = this.findProductById(item.product_id);
      const hasCustom = product && ((product.ingredients_optional?.length || 0) > 0 || (product.ingredients_extras?.length || 0) > 0);
      if (hasCustom) {
        needsCustom.push(item);
      } else {
        this.packSelections.push({ item, removedIngredients: new Set(), selectedExtras: new Set() });
      }
    }

    if (needsCustom.length > 0) {
      this.packCustomizeQueue = needsCustom.slice(1);
      this.packTempItem = needsCustom[0];
      this.packTempRemoved = new Set<number>();
      this.packTempExtras = new Set<number>();
    } else {
      this.finishPackSection(selectedItems.length);
    }
  }

  togglePackRemove(id: number): void {
    if (this.packTempRemoved.has(id)) this.packTempRemoved.delete(id);
    else this.packTempRemoved.add(id);
  }

  togglePackExtra(id: number): void {
    if (this.packTempExtras.has(id)) this.packTempExtras.delete(id);
    else this.packTempExtras.add(id);
  }

  confirmPackItemCustomization(): void {
    if (!this.packTempItem) return;
    this.packSelections.push({
      item: this.packTempItem,
      removedIngredients: new Set(this.packTempRemoved),
      selectedExtras: new Set(this.packTempExtras),
    });

    if (this.packCustomizeQueue.length > 0) {
      this.packTempItem = this.packCustomizeQueue.shift()!;
      this.packTempRemoved = new Set<number>();
      this.packTempExtras = new Set<number>();
    } else {
      this.packTempItem = null;
      const totalAdded = this.packSelections.length - this.packSelectionsSnapshot;
      this.finishPackSection(totalAdded);
    }
  }

  private finishPackSection(itemCount: number): void {
    this.packSelectionsPerSection.push(itemCount);
    this.packCurrentSelected = new Set<number>();
    this.packSectionIndex++;
  }

  packGoBack(): void {
    if (this.packTempItem) {
      // Cancel customization phase — remove items already added for this section
      this.packSelections.length = this.packSelectionsSnapshot;
      this.packTempItem = null;
      this.packCustomizeQueue = [];
      // Stay on same section with selections preserved
      return;
    }
    if (this.packSectionIndex > 0) {
      this.packSectionIndex--;
      const count = this.packSelectionsPerSection.pop() || 0;
      if (count > 0) {
        this.packSelections.splice(-count, count);
      }
      this.packCurrentSelected = new Set<number>();
    } else {
      this.closePackModal();
    }
  }

  confirmPack(): void {
    if (!this.activePack) return;
    const pack = this.activePack;
    const totalPrice = this.packTotalPrice;
    const note = this.packNote.trim();

    // Add each selected item to cart with pack info
    // First item carries the total price, rest carry 0 (TPVSecure convention)
    let isFirst = true;
    for (const sel of this.packSelections) {
      const product = this.findProductById(sel.item.product_id);
      const customizations: any[] = [];

      if (product?.ingredients_optional) {
        for (const ing of product.ingredients_optional) {
          if (sel.removedIngredients.has(ing.id)) {
            customizations.push({ type: 'remove', ingredient: ing.name, description: `Sin ${ing.name}` });
          }
        }
      }
      if (product?.ingredients_extras) {
        for (const extra of product.ingredients_extras) {
          if (sel.selectedExtras.has(extra.id)) {
            customizations.push({ type: 'add', ingredient: extra.name, description: `Extra ${extra.name}` });
          }
        }
      }

      // Pack metadata stored in customization for the cart display
      customizations.push({ type: 'change', ingredient: pack.name, description: `[${pack.name}]` });

      // Collect extra prices for order submission
      const extraPrices: { name: string; price: number }[] = [];
      if (product?.ingredients_extras) {
        for (const extra of product.ingredients_extras) {
          if (sel.selectedExtras.has(extra.id) && extra.extra_price) {
            extraPrices.push({ name: extra.name, price: parseFloat(extra.extra_price) || 0 });
          }
        }
      }

      this.cartService.addItem(
        `pack-${pack.id}-${sel.item.product_id}`,
        `${sel.item.product_name}`,
        isFirst ? totalPrice : 0,
        'Packs',
        customizations,
        isFirst ? note : '',
        undefined,
        {
          pack_id: pack.id,
          pack_name: pack.name,
          real_product_id: sel.item.product_id,
          extra_prices: extraPrices.length > 0 ? extraPrices : undefined,
        }
      );
      isFirst = false;
    }

    this.closePackModal();
  }
}